// ---------------------------------------------------------------------------
// HDDN LAB marketing site — the shell every marketing page renders inside.
//
// Owns: the injected stylesheet, the theme (system default, explicit override
// persisted to localStorage), the skip link, the nav and the footer.
//
// The `.hddn-site` class applied here is what scopes the entire stylesheet.
// Nothing else in the app ever receives it, so none of these styles can reach
// the booking flow or the admin dashboard.
// ---------------------------------------------------------------------------

import React, { useCallback, useEffect, useState } from 'react';
import { siteCss } from './theme.js';
import { STUDIO } from './content.js';
import { NAV_LINKS, go } from './routes.js';

const THEME_KEY = 'hddn_site_theme';

function readStoredTheme() {
  try {
    const v = localStorage.getItem(THEME_KEY);
    return v === 'light' || v === 'dark' ? v : null;
  } catch { return null; }
}

function useTheme() {
  const [theme, setTheme] = useState(readStoredTheme);
  const toggle = useCallback(() => {
    setTheme((cur) => {
      // No stored choice yet: flip away from whatever the system is showing.
      const systemDark = typeof window !== 'undefined'
        && window.matchMedia && window.matchMedia('(prefers-color-scheme: dark)').matches;
      const next = cur ? (cur === 'dark' ? 'light' : 'dark') : (systemDark ? 'light' : 'dark');
      try { localStorage.setItem(THEME_KEY, next); } catch { /* private mode */ }
      return next;
    });
  }, []);
  return [theme, toggle];
}

const IG_PATH = 'M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zM12 0C8.741 0 8.333.014 7.053.072 2.695.272.273 2.69.073 7.052.014 8.333 0 8.741 0 12c0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98C8.333 23.986 8.741 24 12 24c3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98C15.668.014 15.259 0 12 0zm0 5.838a6.162 6.162 0 100 12.324 6.162 6.162 0 000-12.324zM12 16a4 4 0 110-8 4 4 0 010 8zm6.406-11.845a1.44 1.44 0 100 2.881 1.44 1.44 0 000-2.881z';
const TT_PATH = 'M19.59 6.69a4.83 4.83 0 01-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 01-2.88 2.5 2.89 2.89 0 01-2.89-2.89 2.89 2.89 0 012.89-2.89c.28 0 .54.04.79.1v-3.5a6.37 6.37 0 00-.79-.05A6.34 6.34 0 003.15 15.2a6.34 6.34 0 0010.86 4.46V13.2a8.16 8.16 0 004.77 1.52v-3.4a4.85 4.85 0 01-.81.07 4.84 4.84 0 01-2.88-.95v-3.74h4.5z';

function Social({ label, href, path }) {
  return (
    <a href={href} target="_blank" rel="noopener noreferrer" aria-label={label}
       style={{ display: 'inline-flex', padding: 8 }}>
      <svg width="17" height="17" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><path d={path} /></svg>
    </a>
  );
}

function Wordmark() {
  return (
    <a href="/" onClick={go('/')} className="hddn-mark" aria-label="HDDN LAB — home">
      <b>HDDN LAB</b><i>{STUDIO.city}</i>
    </a>
  );
}

function Nav({ path }) {
  const [open, setOpen] = useState(false);
  useEffect(() => { setOpen(false); }, [path]);

  const link = (l) => (
    <a
      key={l.href}
      href={l.href}
      onClick={go(l.href)}
      className="hddn-nav__a"
      aria-current={path === l.href ? 'page' : undefined}
    >
      {l.label}
    </a>
  );

  return (
    <nav className="hddn-nav" aria-label="Primary">
      <div className="hddn-shell">
        <div className="hddn-nav__in">
          <Wordmark />
          <div className="hddn-nav__links">
            {NAV_LINKS.map(link)}
            <a href="/book" onClick={go('/book')} className="hddn-btn hddn-btn--fill" style={{ marginLeft: 10 }}>
              Check availability
            </a>
          </div>
          <button
            className="hddn-nav__burger"
            aria-expanded={open}
            aria-controls="hddn-drawer"
            onClick={() => setOpen((v) => !v)}
          >
            {open ? 'Close' : 'Menu'}
            <svg width="16" height="12" viewBox="0 0 16 12" aria-hidden="true" fill="none" stroke="currentColor" strokeWidth="2">
              {open
                ? <><path d="M2 1l12 10" /><path d="M14 1L2 11" /></>
                : <><path d="M0 1.5h16" /><path d="M0 6h16" /><path d="M0 10.5h16" /></>}
            </svg>
          </button>
        </div>
        {open ? (
          <div className="hddn-drawer" id="hddn-drawer">
            {NAV_LINKS.map(link)}
            <a href="/book" onClick={go('/book')} className="hddn-btn hddn-btn--fill hddn-btn--wide" style={{ marginTop: 12 }}>
              Check availability
            </a>
          </div>
        ) : null}
      </div>
    </nav>
  );
}

function Footer({ theme, onToggleTheme }) {
  const year = new Date().getFullYear();
  return (
    <footer className="hddn-foot">
      <div className="hddn-shell">
        <div className="hddn-foot__cols">
          <div>
            <p className="hddn-h3">{STUDIO.tagline}</p>
            <address style={{ fontStyle: 'normal', marginTop: 16, color: 'var(--ground-muted)' }}>
              {STUDIO.street}<br />
              {STUDIO.city}, {STUDIO.state} {STUDIO.zip}<br />
              <a href={`mailto:${STUDIO.email}`} style={{ color: 'var(--accent)' }}>{STUDIO.email}</a>
            </address>
            <div style={{ display: 'flex', gap: 4, marginTop: 10, marginLeft: -8 }}>
              <Social label="HDDN LAB on Instagram" href={STUDIO.instagram} path={IG_PATH} />
              <Social label="HDDN LAB on TikTok" href={STUDIO.tiktok} path={TT_PATH} />
            </div>
          </div>

          <div>
            <p className="hddn-label" style={{ color: 'var(--accent)' }}>Hire us</p>
            <ul style={{ marginTop: 12, display: 'grid', gap: 9 }}>
              <li><a href="/content" onClick={go('/content')}>Content creation</a></li>
              <li><a href="/photographer" onClick={go('/photographer')}>Book a photographer</a></li>
              <li><a href="/work" onClick={go('/work')}>The work</a></li>
            </ul>
          </div>

          <div>
            <p className="hddn-label" style={{ color: 'var(--accent)' }}>Book the room</p>
            <ul style={{ marginTop: 12, display: 'grid', gap: 9 }}>
              <li><a href="/studio" onClick={go('/studio')}>The studio &amp; rates</a></li>
              <li><a href="/book" onClick={go('/book')}>Availability</a></li>
              <li><a href="/about" onClick={go('/about')}>About</a></li>
              <li><a href="/careers" onClick={go('/careers')}>Careers</a></li>
            </ul>
          </div>
        </div>

        <div className="hddn-foot__base">
          <span>© {year} {STUDIO.name} · {STUDIO.hoursLabel}</span>
          <span style={{ display: 'flex', gap: 18, alignItems: 'center' }}>
            <a href="/privacy">Privacy</a>
            <button
              onClick={onToggleTheme}
              className="hddn-nav__a"
              style={{ padding: '6px 0' }}
              aria-pressed={theme === 'dark'}
            >
              {theme === 'dark' ? 'Light mode' : 'Dark mode'}
            </button>
          </span>
        </div>
      </div>
    </footer>
  );
}

export default function SiteShell({ route, path }) {
  const [theme, toggleTheme] = useTheme();
  const Page = route.component;

  useEffect(() => {
    // The server injects the real per-route metadata; this keeps the title
    // correct after a client-side navigation, which never hits the server.
    if (route.meta && route.meta.title) document.title = route.meta.title;
  }, [route]);

  return (
    <div className="hddn-site" data-theme={theme || undefined}>
      <style>{siteCss}</style>
      <a className="hddn-skip" href="#main">Skip to content</a>
      <Nav path={path} />
      <main id="main">
        <Page />
      </main>
      <Footer theme={theme} onToggleTheme={toggleTheme} />
    </div>
  );
}
